import React, { createRef, FC, ForwardedRef, forwardRef, useMemo, useState } from "react";
import { Button, Text, Box, Flex, Input, ButtonProps } from "@chakra-ui/react";
import { useDebounce, useMount } from "react-use";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

import { colours } from "@/utils/themeUtils";
import { ONE_DIGIT, padTimes, VALID_FORMATS_PRESELECTED, validate, ZERO_TO_TWO_DIGITS } from "./ClassroomSessionTimeSelectorHelpers";

dayjs.extend(customParseFormat);

interface TimeSelectorProps {
	preselected?: string | string[] | number | number[] | null;
	onChange: (value: number | null, error: string | null) => void;
	options?: {
		validateFuture?: boolean;
		minTime?: string | number | null;
		maxTime?: string | number | null;
		autoFocus?: boolean;
	};
}

export const ClassroomSessionTimeSelector: FC<TimeSelectorProps> = (props) => {
	// Lazy initialise preselected time
	const init = () => {
		if (props.preselected) {
			const timeObject = Array.isArray(props.preselected)
				? // Monitoring class somehow is an array
					dayjs(padTimes(props.preselected[0]), VALID_FORMATS_PRESELECTED, true)
				: dayjs(padTimes(props.preselected), VALID_FORMATS_PRESELECTED, true);
			if (timeObject.isValid()) {
				return {
					initHour: timeObject.format("hh"),
					initMinute: timeObject.format("mm"),
					initPeriod: timeObject.format("A"),
					initError: null,
				};
			}
		}
		return {
			initHour: "",
			initMinute: "",
			initPeriod: "AM",
			initError: null,
		};
	};

	const inputHourRef = createRef<HTMLInputElement>();
	const inputMinuteRef = createRef<HTMLInputElement>();

	const { initHour, initMinute, initPeriod, initError } = init();
	const [inputHour, setInputHour] = useState(initHour);
	const [inputMinute, setInputMinute] = useState(initMinute);
	const [inputPeriod, setInputPeriod] = useState(initPeriod);
	const [error, setError] = useState<string | null>(initError);

	useMount(() => {
		// Optional, default on - focus on the hour input when the component is mounted
		const shouldAutoFocus = props.options?.autoFocus ?? true;
		if (shouldAutoFocus) {
			inputHourRef.current?.focus();
		}
	});

	const shouldValidateFuture = useMemo(() => props.options?.validateFuture ?? true, [props.options?.validateFuture]);

	const minTimeObject = useMemo(() => {
		if (props.options?.minTime) {
			return dayjs(padTimes(props.options.minTime), VALID_FORMATS_PRESELECTED, true).add(1, "minute");
		}
		return undefined;
	}, [props.options?.minTime]);

	const maxTimeObject = useMemo(() => {
		if (props.options?.maxTime) {
			return dayjs(padTimes(props.options.maxTime), VALID_FORMATS_PRESELECTED, true).subtract(1, "minute");
		}
		return undefined;
	}, [props.options?.maxTime]);

	const process = () => {
		const { result, error } = validate(inputHour, inputMinute, inputPeriod, shouldValidateFuture, minTimeObject, maxTimeObject);
		setError(error);
		props.onChange(result, error);
	};

	useMount(process);

	useDebounce(process, 100, [inputHour, inputMinute, inputPeriod, minTimeObject, maxTimeObject]);

	return (
		<Box>
			<Flex alignItems="center" gap={1}>
				<Input
					data-testid="hour-input"
					ref={inputHourRef}
					value={inputHour}
					maxLength={2}
					placeholder="hh"
					bg="white"
					width="40px"
					px={2.5}
					py={0}
					// Prepend 0 if single digit is entered
					onBlur={(e) => {
						if (ONE_DIGIT.test(e.target.value)) {
							setInputHour(`0${e.target.value}`);
						}
					}}
					onChange={(e) => {
						// Only allow 0-2 numeric digits
						if (!ZERO_TO_TWO_DIGITS.test(e.target.value)) {
							return;
						}
						// Limit the input to 2 digits
						if (e.target.value.length <= 2) {
							setInputHour(e.target.value);
						}
						// Move to the minute box after entering a time into the hour box
						if (e.target.value.length === 2) {
							inputMinuteRef.current?.focus();
						}
					}}
					// Select all text when the input is clicked
					onFocus={(e) => {
						e.target.select();
					}}
				/>
				<Text>:</Text>
				<Input
					data-testid="minute-input"
					ref={inputMinuteRef}
					value={inputMinute}
					maxLength={2}
					placeholder="mm"
					bg="white"
					width="40px"
					px={2.5}
					py={0}
					// Prepend 0 if single digit is entered
					onBlur={(e) => {
						if (ONE_DIGIT.test(e.target.value)) {
							setInputMinute(`0${e.target.value}`);
						}
					}}
					onChange={(e) => {
						// Only allow 0-2 numeric digits
						if (!ZERO_TO_TWO_DIGITS.test(e.target.value)) {
							return;
						}
						// Limit the input to 2 digits
						if (e.target.value.length <= 2) {
							setInputMinute(e.target.value);
						}
					}}
					// Select all text when the input is clicked
					onFocus={(e) => {
						e.target.select();
					}}
				/>
				<Flex alignItems="center" ml={1}>
					<PeriodButton
						active={inputPeriod === "AM"}
						onClick={() => {
							setInputPeriod("AM");
						}}
					>
						AM
					</PeriodButton>
					<PeriodButton
						active={inputPeriod === "PM"}
						onClick={() => {
							setInputPeriod("PM");
						}}
					>
						PM
					</PeriodButton>
				</Flex>
			</Flex>
			{error && (
				<Text className="error" color={colours.danger.background.strong} width="209px" p="4px 0 0 4px">
					{error}
				</Text>
			)}
		</Box>
	);
};

interface PeriodButtonProps extends ButtonProps {
	active: boolean;
}

const PeriodButton = forwardRef(({ active, onClick, children }: PeriodButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {
	let testId = undefined;
	if (typeof children === "string") {
		if (children.toLowerCase() === "am") {
			testId = "am-button";
		} else if (children.toLowerCase() === "pm") {
			testId = "pm-button";
		}
	}
	return (
		<Button
			ref={ref}
			data-testid={testId}
			data-test-active={active}
			onClick={onClick}
			width="40px"
			fontSize={"sm"}
			py={0}
			color={active ? "neutrals.0" : "neutrals.900"}
			bgColor={active ? "neutrals.500" : "neutrals.0"}
			borderColor={active ? "neutrals.500" : "neutrals.50"}
			_first={{
				borderRadius: "8px 0 0 8px",
				borderWidth: "1px 0 1px 1px",
			}}
			_last={{
				borderRadius: "0 8px 8px 0",
				borderWidth: "1px 1px 1px 0",
			}}
		>
			{children}
		</Button>
	);
});
